import React, { useEffect, useState } from 'react';
import BlazeTable from '../../../../../../../components/blazeTable/BlazeTable';
import { BlazeTableOptions } from '../../../../../../../components/blazeTable/BlazeTable.types';
import _ from 'lodash';
import { getSelectedCountTitle } from '../../../../../../../utils/miscUtil';
import {
  deleteRecordingsByIds,
  getExistingRecordingMessages,
  pollForNewRecordingMessages,
} from '../../../../../../../store/mockServices/mockServiceActions';
import { delay } from 'q';
import { InspectionViewRequest, TxiViewRequest } from '../../../../../../../types/rest/analyticsTypes';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../../../../../store/rootReducer';
import { useIsMount } from '../../../../../../../hooks/useIsMountHook';
import { MockService } from '../../../../../../../types/rest/mockServiceTypes';

import i18n from '../../../../../../../i18n';
import { getColoredHttpMethod, getPath } from '../../../../../../../utils/commonUtil';
import CopyToClipboardButton from '../../../../../../../components/buttons/CopyToClipboardButton';
import { nestedTableCell } from '../../../../../../Analytics/AnalyticsMockService/Inspection/InspectionViewTransactional';
import BzmIcon from '../../../../../../../components/icons/BzmIcon';
import { confirm } from '../../../../../../../components/ui/modal/BlazeConfirmationModal';
import { updateHttpRecordedMessages } from '../../../../../../../store/mockServices/mockServiceDetail/mockServiceDetailActions';
import ElevioTooltip from '../../../../../../../components/overlays/helpTooltip/TitleHelpTooltip/ElevioTooltip';
import HttpTransactionDetail from './Detail/HttpTransactionDetail';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { Button, Form } from 'react-bootstrap';
import SimpleLoader from '../../../../../../../components/loaders/SimpleLoader';
import { showToastNotification } from '../../../../../../../store/ui/toasts/toastsActions';
import { ToastType } from '../../../../../../../store/ui/toasts/toastsTypes';
import FullPageLoader from '../../../../../../../components/loaders/FullPageLoader';
import Placeholder from '../../../../../../../components/placeholder/Placeholder';
import ConvertHttpRecordingModal from './ConvertHttpRecordingModal';

interface Props {
  disabled: boolean;
  mockService: MockService;
}

const HttpRecordings = ({ disabled, mockService }: Props) => {
  const isRecording = mockService.httpRecorderStatus === 'RUNNING';
  const [selected, setSelected] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const isPolling: boolean = useSelector(
    (state: ApplicationState) => state.mockServiceDetails.httpRecordings.isPolling,
  );
  const isSaving: boolean = useSelector((state: ApplicationState) => state.mockServiceDetails.httpRecordings.isSaving);
  const isMount = useIsMount();
  const dispatch = useDispatch();
  const [isHttpConvertedOpened, setIsHttpConvertedOpened] = useState(false);

  const recordedHttpTransaction: InspectionViewRequest[] = useSelector(
    (state: ApplicationState) => state.mockServiceDetails.httpRecordings.messages,
  );
  const totalHttpTransactions: number = useSelector(
    (state: ApplicationState) => state.mockServiceDetails.httpRecordings.totalMessages,
  );

  useEffect(() => {
    if (isMount) {
      initComponentOnMount();
    }
    return () => {
      //Cleanup
      console.log('Unmounting RecordedMessages component...');
    };
  }, []);

  const initComponentOnMount = async () => {
    //Get the existing transactions if there are none yet
    if (recordedHttpTransaction.length === 0) {
      setIsLoading(true);
      await dispatch(getExistingRecordingMessages(mockService, 'MOCK_REQUEST'));
      setIsLoading(false);
    }

    //Resume polling with delay if not running
    await delay(4000);
    if (isRecording && !isPolling) {
      dispatch(pollForNewRecordingMessages(mockService, 'MOCK_REQUEST'));
    }
  };

  const selectHttpTransactionsRecorded = (items: any[]) => {
    const ids = _.map(items, item => item.id);
    setSelected(ids);
  };

  const deleteHttpRecording = (id?: string) => {
    //Show Confirmation Dialog
    confirm({
      confirmation: <span>Are you sure about deleting the selected Http transaction(s)?</span>,
      yesOptionTitle: 'Delete',
      title: 'Delete Http transaction from Recording',
    }).then(async () => {
      const toBeDeleted: any[] = id ? [id] : selected;
      dispatch(deleteRecordingsByIds(mockService, toBeDeleted, 'MOCK_REQUEST'));
    });
  };

  const onSaveRecording = () => {
    if (_.size(selected) === 0) {
      dispatch(showToastNotification(ToastType.Warning, 'Please select at least one transaction to save.'));
      return;
    }
    setIsHttpConvertedOpened(true);
  };

  const getTableOptions = (): BlazeTableOptions => ({
    expandDetailComponent: (httpTxRecorded: InspectionViewRequest) => {
      return <HttpTransactionDetail httpTransactionRecorded={httpTxRecorded} requestArgs={httpTxRecorded.arguments} />;
    },
    pageable: false,
    selectable: true,
    showRowExpander: true,
    columnDef: [
      {
        header: 'Id',
        accessor: 'id',
        cellClass: 'flex-table-col-overflow-visible',
      },
      {
        header: i18n.t('analytics:txInspection.table.method'),
        width: 80,
        accessor: 'operation',
        cellComponent: (request: TxiViewRequest) => {
          return getColoredHttpMethod(request.operation as any);
        },
      },
      {
        header: i18n.t('analytics:txInspection.table.requestPath'),
        cellComponent: (request: TxiViewRequest) => {
          return (
            <div id={`requestPathValue` + request.id} title={request.url}>
              {!_.isNil(request.url) ? getPath(request.url) : null}
              <div className="flex-table-actions ml-2">
                <CopyToClipboardButton
                  textToCopy={request.url}
                  title={i18n.t('analytics:txInspection.table.copyRequestPath')}
                  id={`requestPathCopyButton` + request.id}
                >
                  <span
                    className="icon-bzm-clipboard btn-link-dark icon-xs text-blue"
                    aria-hidden="true"
                    style={{ color: 'inherit' }}
                  />
                </CopyToClipboardButton>
              </div>
            </div>
          );
        },
        accessor: 'url',
      },
      {
        header: i18n.t('analytics:txInspection.table.queryParameter'),
        cellComponent: (request: TxiViewRequest) => {
          return nestedTableCell(
            request.arguments && request.arguments.length > 0 ? [request.arguments[0]] : request.arguments,
            'key',
          );
        },
        accessor: 'queryParameter',
      },
      {
        header: i18n.t('analytics:txInspection.table.queryParameterValue'),
        cellComponent: (request: TxiViewRequest) => {
          return nestedTableCell(
            request.arguments && request.arguments.length > 0 ? [request.arguments[0]] : request.arguments,
            'value',
          );
        },
        accessor: 'queryParameterValue',
      },
      {
        header: 'Actions',
        accessor: '-',
        width: 80,
        cellComponent: (msg: InspectionViewRequest) => {
          return (
            <div className={'flex-table-actions'}>
              <button className={'btn btn-link-dark btn-no-hover'} onClick={() => deleteHttpRecording(msg.id)}>
                <BzmIcon variant={'icon-bzm-delete'} className={'icon-xs'} />
              </button>
            </div>
          );
        },
        isSortable: true,
      },
    ],
  });

  const getTable = () => {
    return (
      <div>
        <Row>
          <Col lg={4} md={6}>
            <></>
          </Col>
          <Col lg={8} sm={6}>
            <Button
              variant={'primary'}
              disabled={isSaving}
              className={'position-absolute'}
              style={{ right: 16, top: -46 }}
              onClick={onSaveRecording}
            >
              {isSaving && <SimpleLoader className={'mr-3 d-inline-block'} showLoadingText={false} />}
              {!isSaving && <BzmIcon variant={'icon-bzm-save'} className={'mr-3'} />}
              Save Transactions
            </Button>
          </Col>
        </Row>
        <h2 className={'text-uppercase mb-1'}>
          <span className={'mr-2'}>Recorded Transactions</span>
          {getSelectedCountTitle(_.size(recordedHttpTransaction), _.size(selected))}
          <ElevioTooltip elevioId={'Transactions_heading_help'} id={'TransactionsHelpId'} />
          <button
            id={`blk-delete`}
            className="btn btn-link btn-no-hover p-0 mx-2"
            title={'Remove Selected Transactions'}
            onClick={() => deleteHttpRecording()}
            disabled={disabled || _.isEmpty(selected)}
          >
            <BzmIcon variant={'icon-bzm-delete'} className={'icon-sm'} />
            <span className="sr-only">{i18n.t('transactions:table.removeTxns')}</span>
          </button>
        </h2>
        <p>List of recorded transactions</p>
        <BlazeTable
          selectedItems={selected}
          onSelectionChange={selectHttpTransactionsRecorded}
          isLoading={isLoading}
          options={getTableOptions()}
          data={recordedHttpTransaction}
          showPageSizeDropdown={true}
        />
      </div>
    );
  };

  return (
    <div>
      <ConvertHttpRecordingModal
        recordingIds={selected}
        isConvertedOpen={isHttpConvertedOpened}
        recordingTags={[]}
        onCloseModal={() => setIsHttpConvertedOpened(false)}
      />
      {isLoading && <FullPageLoader loadingText="Loading Recorded Transactions..." className={'text-center'} />}

      {!isLoading && (
        <>
          {_.size(recordedHttpTransaction) === 0 ? (
            <div className={'mt-8 mb-8'}>
              <Placeholder iconSize={85} titleSize={20} title={'No Recorded Transactions Yet'} icon={'info'} />
            </div>
          ) : (
            <div>{getTable()}</div>
          )}
        </>
      )}
    </div>
  );
};

export default HttpRecordings;
